/**
 * useSEO.js
 * Hook qui injecte dynamiquement toutes les balises SEO dans le <head>
 * sans dépendance externe (pas besoin de react-helmet).
 *
 * Usage :
 *   useSEO({ title, description, path, type, imageUrl })
 */
import { useEffect } from 'react';

const SITE_NAME   = 'Alaia Cristal Bien-être - Spa Bessines';
const BASE_URL    = 'https://www.alaia-cristal-bienetre.fr/';           // ← adapter à ton domaine
const DEFAULT_IMG = `${BASE_URL}/logo.jpg`;           // ← ajouter cette image dans /public
const PHONE       = '+33647139304';
const ADDRESS     = '21 Rue des Charmes, 79000 Bessines';
const CITY        = 'Bessines';
const REGION      = 'Deux-Sèvres';
const COUNTRY     = 'FR';
const POSTAL      = '79000';
const LATITUDE    = '46.3786';
const LONGITUDE   = '-0.2213';

/* ─── Helpers DOM ─── */
const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setScript = (id, json) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('id', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(json);
};

/* ─── Hook principal ─── */
const useSEO = ({
  title,
  description,
  path = '',
  type = 'website',
  imageUrl = DEFAULT_IMG,
  noIndex = false,
} = {}) => {
  useEffect(() => {
    const fullTitle       = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Massages & Soins bien-être à Bessines`;
    const fullDescription = description ||
      'Spa bien-être à Bessines (79) - Alaia Cristal Bien-être propose des massages du monde, soins visage, rituels corps et cartes cadeaux dans un cadre sensoriel unique au Spa Eclipsea de Bessines (79).';
    const canonical       = `${BASE_URL}${path}`;

    /* ── Title ── */
    document.title = fullTitle;

    /* ── Meta description & robots ── */
    setMeta('description',       fullDescription);
    setMeta('robots',            noIndex ? 'noindex,nofollow' : 'index,follow');
    setMeta('author',            SITE_NAME);
    setMeta('keywords',
      'massage bien-être Bessines, spa Bessines, massage corps Bessines, soin visage Bessines, ' +
      'massage relaxant Nouvelle-Aquitaine, Alaia Cristal, Eclipsea spa, lithothérapie, ' +
      'massage lomi lomi, massage énergétique, massage intuitif' +
      'soin visage lithothérapie, carte cadeau massage, massages 79, massage Deux-Sèvres' + 
      'spa deux-sèvres, spa 79, eclipsea spa, spa privatif bessines, spa bessines, spa bessines 79, massages bessines'
    );

    /* ── Canonical ── */
    setLink('canonical', canonical);

    /* ── Open Graph ── */
    setMeta('og:type',        type,             'property');
    setMeta('og:site_name',   SITE_NAME,        'property');
    setMeta('og:title',       fullTitle,        'property');
    setMeta('og:description', fullDescription,  'property');
    setMeta('og:url',         canonical,        'property');
    setMeta('og:image',       imageUrl,         'property');
    setMeta('og:image:width',  '1200',          'property');
    setMeta('og:image:height', '630',           'property');
    setMeta('og:locale',      'fr_FR',          'property');

    /* ── Twitter Card ── */
    setMeta('twitter:card',        'summary_large_image');
    setMeta('twitter:title',       fullTitle);
    setMeta('twitter:description', fullDescription);
    setMeta('twitter:image',       imageUrl);

    /* ── Données structurées : LocalBusiness ── */
    setScript('sd-localbusiness', {
      '@context': 'https://schema.org',
      '@type': ['HealthAndBeautyBusiness', 'SpaOrBeautyParlor'],
      '@id': BASE_URL,
      name: SITE_NAME,
      alternateName: 'Alaia Cristal',
      description: fullDescription,
      url: BASE_URL,
      telephone: PHONE,
      email: 'alaia.cristalbienetre@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: ADDRESS,
        addressLocality: CITY,
        addressRegion: REGION,
        postalCode: POSTAL,
        addressCountry: COUNTRY,
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: LATITUDE,
        longitude: LONGITUDE,
      },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Tuesday',
          opens: '14:00', closes: '20:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Wednesday','Thursday','Friday'],
          opens: '11:00', closes: '20:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday',
          opens: '10:00', closes: '18:00' },
      ],
      priceRange: '€€',
      currenciesAccepted: 'EUR',
      paymentAccepted: 'Cash, Card',
      image: imageUrl,
      sameAs: [],
      hasMap: `https://maps.google.com/?q=${encodeURIComponent(ADDRESS)}`,
    });

    /* ── Données structurées : BreadcrumbList ── */
    const crumbs = [{ name: 'Accueil', url: BASE_URL }];
    if (path && path !== '/') {
      const labels = {
        '/cartes-cadeaux': 'Cartes Cadeaux',
        '/mentions-legales': 'Mentions Légales',
        '/politique-confidentialite': 'Politique de Confidentialité',
        '/politique-cookies': 'Politique de Cookies',
        '/cgv': 'CGV',
      };
      crumbs.push({ name: labels[path] || path, url: canonical });
    }
    setScript('sd-breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.name,
        item: c.url,
      })),
    });

  }, [title, description, path, type, imageUrl, noIndex]);
};

export default useSEO;